import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { getContent, getContentWithoutState, patchContent, postContent } from "../../../helpers/FetchContent";
import SpinnerSmall from "../../SpinnerSmall";
import { useUserStore } from "../../../stores/users-store";
import ErrorMsgComponent from "../../../helpers/Errors";
import { displayApiError } from "../../../helpers/DisplayAPIError";
import { validateUserForm } from "../../../helpers/Validators";
import { userCreateInitialData, userUpdateInitialData } from "../../../constants/inits";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/solid";

export default function UserForm(props) {
	const [errMsg, setErrMsg] = useState("");
	const setUsers = useUserStore(state => state.setUsers);
	const filters = useUserStore(state => state.filters);
	const [loading, setLoading] = useState(false);
	const [isPasswordHidden, setIsPasswordHidden] = useState(true);
	const [formData, updateFormData] = useState(props.isEdit ? userUpdateInitialData : userCreateInitialData);
	const [formErrors, setFormErrors] = useState({});
	const [isSubmitted, setIsSubmitted] = useState(false);

	// const password = useMemo(() => {
	// 	return generatePassword();
	// }, []);

	useEffect(() => {
		if (props.open && props.isEdit) {
			getUser();
		}

		if (props.open) {
			updateFormData(userCreateInitialData);
		}
	}, [props.open, props.user_id]);

	const cancelButtonRef = useRef(null);

	const handleChange = e => {
		updateFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};
	const handleActiveCheck = e => {
		updateFormData({
			...formData,
			[e.target.name]: e.target.checked
		});
	};

	const getUser = async () => {
		setErrMsg("");

		const response = await getContentWithoutState(
			`${process.env.REACT_APP_NEXT_PUBLIC_API_URL}/v1/users/` + props.user_id
		);

		if (response.id) {
			updateFormData({
				first_name: response.first_name,
				last_name: response.last_name,
				active: response.active,
				level: response.level
			});
		} else {
			setErrMsg("Unable to fetch user");
		}
	};

	const getUsers = async () => {
		await getContent(
			`${process.env.REACT_APP_NEXT_PUBLIC_API_URL}/v1/users/?name=${filters.name}&email=${filters.email}`,
			setUsers
		);
	};

	const handleSubmit = async e => {
		e.preventDefault();
		setLoading(true);
		setErrMsg("");
		setFormErrors({});

		const errors = await validateUserForm(formData, props.isEdit);
		setFormErrors(errors);
		setIsSubmitted(true);

		if (Object.keys(errors).length === 0) {
			formData.level = Number(formData.level);

			let response;

			if (props.isEdit) {
				response = await patchContent(
					process.env.REACT_APP_NEXT_PUBLIC_API_URL + "/v1/users/" + props.user_id,
					formData
				);
			} else {
				response = await postContent(process.env.REACT_APP_NEXT_PUBLIC_API_URL + "/v1/users/", formData);
			}

			if (response.ok) {
				await getUsers();
				props.onClose();
			} else {
				// Show endpoint error if exists
				const err = await response.json();
				setErrMsg(err.error ? displayApiError(err.error) : "Unable to update user");
			}
		}

		setLoading(false);
	};

	const resetForm = () => {
		setFormErrors({});
		updateFormData(userCreateInitialData);
		props.onClose();
	};

	return (
		<>
			<Transition.Root show={props.open} as={Fragment}>
				<Dialog
					as="div"
					className="fixed z-10 inset-0 overflow-y-auto"
					initialFocus={cancelButtonRef}
					onClose={resetForm}
				>
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all md:my-8 md:align-middle md:max-w-6xl md:w-full">
								<div className="bg-white px-4 pt-5 pb-4 md:p-16 md:pb-14">
									<div className="lg:flex lg:items-start">
										<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
											<Dialog.Title
												as="h3"
												className="text-lg leading-6 font-medium text-gray-900"
											>
												{props.isEdit ? "Update" : "Create"} User
											</Dialog.Title>
											<div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
												<div className="md:grid md:grid-cols-3 md:gap-6">
													<div className="md:col-span-1">
														<p className="mt-1 text-sm text-gray-500">
															Please fill in the necessary information to
															{props.isEdit ? " update" : " create"} User
														</p>
													</div>
													<div className="mt-5 md:mt-0 md:col-span-2">
														<form action="#" method="POST">
															<div className="grid grid-cols-6 gap-6">
																{props.isEdit && (
																	<div className="active col-span-6">
																		<div className="mr-4">Active</div>
																		<label className="relative inline-flex cursor-pointer items-center">
																			<input
																				value={formData.active}
																				checked={formData.active}
																				name="active"
																				id="active"
																				onChange={handleActiveCheck}
																				type="checkbox"
																				className="peer sr-only"
																			/>
																			<label
																				htmlFor="active"
																				className="hidden"
																			></label>
																			<div
																				id="active_switch"
																				className="peer h-6 w-11 rounded-full border bg-slate-200 after:absolute after:left-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-green-300"
																			></div>
																		</label>
																	</div>
																)}
																{!props.isEdit && (
																	<div className="col-span-3">
																		<label
																			htmlFor="create_user_email"
																			className="block text-sm font-medium text-gray-700"
																		>
																			User Email
																		</label>
																		<input
																			type="text"
																			name="email"
																			id="create_user_email"
																			onChange={handleChange}
																			className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.email && "border-red-700"}`}
																		/>
																		{isSubmitted && formErrors.email && (
																			<span className="text-red-700 text-sm">
																				{formErrors.email}
																			</span>
																		)}
																	</div>
																)}
																<div className="col-span-3">
																	<label
																		htmlFor="first_name"
																		className="block text-sm font-medium text-gray-700"
																	>
																		User First Name
																	</label>
																	<input
																		value={formData.first_name}
																		type="text"
																		name="first_name"
																		id="first_name"
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.first_name && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.first_name && (
																		<span className="text-red-700 text-sm">
																			{formErrors.first_name}
																		</span>
																	)}
																</div>
																<div className="col-span-3">
																	<label
																		htmlFor="last_name"
																		className="block text-sm font-medium text-gray-700"
																	>
																		User Last Name
																	</label>
																	<input
																		value={formData.last_name}
																		type="text"
																		name="last_name"
																		id="last_name"
																		onChange={handleChange}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.last_name && "border-red-700"}`}
																	/>
																	{isSubmitted && formErrors.last_name && (
																		<span className="text-red-700 text-sm">
																			{formErrors.last_name}
																		</span>
																	)}
																</div>
																{!props.isEdit && (
																	<div className="col-span-3">
																		<label
																			htmlFor="password"
																			className="block text-sm font-medium text-gray-700"
																		>
																			User Password
																		</label>
																		<div className="relative">
																			<input
																				type={
																					isPasswordHidden
																						? "password"
																						: "text"
																				}
																				name="password"
																				id="password"
																				className={`mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.password && "border-red-700"}`}
																				value={formData.password}
																				onChange={handleChange}
																			/>
																			{isPasswordHidden ? (
																				<EyeIcon
																					className="h-7 w-7 absolute inset-y-1 right-0 pr-3 flex items-center cursor-pointer"
																					onClick={() =>
																						setIsPasswordHidden(
																							!isPasswordHidden
																						)
																					}
																				></EyeIcon>
																			) : (
																				<EyeOffIcon
																					className="h-7 w-7 absolute inset-y-1 right-0 pr-3 flex items-center cursor-pointer"
																					onClick={() =>
																						setIsPasswordHidden(
																							!isPasswordHidden
																						)
																					}
																				></EyeOffIcon>
																			)}
																			{isSubmitted && formErrors.password && (
																				<span className="text-red-700 text-sm">
																					{formErrors.password}
																				</span>
																			)}
																		</div>
																	</div>
																)}
																<div className="col-span-3">
																	<label
																		htmlFor="level"
																		className="block text-sm font-medium text-gray-700"
																	>
																		User Level
																	</label>
																	<select
																		id="level"
																		name="level"
																		onChange={handleChange}
																		value={formData.level}
																		className={`"mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md ${isSubmitted && formErrors.level && "border-red-700"}`}
																	>
																		<option key="default" value="" disabled hidden>
																			Select User Level
																		</option>
																		<option key="1" value="1">
																			User
																		</option>
																		<option key="99" value="99">
																			Admin
																		</option>
																	</select>
																	{isSubmitted && formErrors.level && (
																		<span className="text-red-700 text-sm">
																			{formErrors.level}
																		</span>
																	)}
																</div>
															</div>
														</form>
														{errMsg && <ErrorMsgComponent errMsg={errMsg} />}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
									<button
										type="button"
										className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
										onClick={handleSubmit}
									>
										{loading && <SpinnerSmall />}
										{!loading && (props.isEdit ? "Update" : "Create")}
									</button>
									<button
										type="button"
										className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
										onClick={resetForm}
										ref={cancelButtonRef}
									>
										Cancel
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</>
	);
}
